import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Clock, Truck, Package, MapPin, Phone } from 'lucide-react';
import { formatDateWithDay } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ETAPES_ICONS = {
  a_preparer:       { emoji: '✅', color: '#22C55E' },
  en_preparation:   { emoji: '📦', color: '#F59E0B' },
  pret_depart:      { emoji: '🚛', color: '#3B82F6' },
  en_route:         { emoji: '🛣️', color: '#8B5CF6' },
  installe:         { emoji: '🎉', color: '#22C55E' },
  retour_recupere:  { emoji: '🔄', color: '#D4AF37' },
};

export default function SuiviLivraisonPage() {
  const { trackingToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/tracking/${trackingToken}`);
        if (!res.ok) { setError(true); return; }
        setData(await res.json());
      } catch { setError(true); }
      finally { setLoading(false); }
    };
    load();
    // Refresh toutes les 60 secondes
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [trackingToken]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1E20' }}>
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Chargement…</p>
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#0B1E20' }}>
      <div className="text-center">
        <p className="font-display text-xl font-bold mb-2" style={{ color: '#FFFFFF' }}>Suivi introuvable</p>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Le lien de suivi est invalide ou expiré.</p>
      </div>
    </div>
  );

  const etapeDone = data.etapes.filter(e => e.done).length;
  const progress = Math.round(etapeDone / data.etapes.length * 100);
  const currentEtape = data.etapes.find(e => e.current) || data.etapes.find(e => e.done);

  return (
    <div className="min-h-screen" style={{ background: '#0B1E20' }}>
      {/* Header */}
      <div className="px-4 py-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <img src="https://customer-assets.emergentagent.com/job_event-maestro-95/artifacts/hu1fdxw0_Logo%20fe%CC%82te%20-%20show%20e%CC%81ve%CC%80nement%20-2.png"
              alt="Show Événement" className="h-8" />
          </div>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Suivi de votre livraison en temps réel</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">

        {/* Statut principal */}
        <div className="rounded-2xl p-6 text-center"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="text-5xl mb-3">{ETAPES_ICONS[data.statut]?.emoji || '🚛'}</div>
          <p className="font-display text-xl font-bold mb-1" style={{ color: '#FFFFFF' }}>
            {currentEtape?.label || 'En préparation'}
          </p>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Chauffeur : {data.chauffeur}
          </p>
          {data.heure_livraison && (
            <p className="text-xs mt-1" style={{ color: '#D4AF37' }}>
              <Clock size={11} className="inline mr-1" />Livraison prévue : {data.heure_livraison}
            </p>
          )}
        </div>

        {/* Barre progression */}
        <div>
          <div className="flex justify-between text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <span>Progression</span>
            <span style={{ color: '#D4AF37' }}>{progress}%</span>
          </div>
          <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div className="h-full rounded-full transition-all duration-700"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #D4AF37, #22C55E)' }} />
          </div>
        </div>

        {/* Étapes */}
        <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {data.etapes.map((etape, i) => {
            const iconConf = ETAPES_ICONS[etape.key] || { emoji: '⭐', color: '#888' };
            return (
              <div key={i} className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: i < data.etapes.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: etape.done ? `${iconConf.color}20` : 'rgba(255,255,255,0.06)',
                    border: etape.current ? `2px solid ${iconConf.color}` : etape.done ? `1px solid ${iconConf.color}40` : '1px solid rgba(255,255,255,0.1)',
                  }}>
                  {etape.done ? (
                    <span className="text-sm">{iconConf.emoji}</span>
                  ) : (
                    <div className="w-2 h-2 rounded-full" style={{ background: etape.current ? '#D4AF37' : 'rgba(255,255,255,0.2)' }} />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold"
                    style={{ color: etape.done ? '#FFFFFF' : etape.current ? '#D4AF37' : 'rgba(255,255,255,0.3)' }}>
                    {etape.label}
                  </p>
                  {etape.current && (
                    <p className="text-xs" style={{ color: '#D4AF37' }}>En cours…</p>
                  )}
                </div>
                {etape.done && <Check size={14} style={{ color: '#22C55E', flexShrink: 0 }} />}
              </div>
            );
          })}
        </div>

        {/* Matériel */}
        {data.items?.length > 0 && (
          <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-xs font-bold mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>MATÉRIEL COMMANDÉ</p>
            {data.items.map((item, i) => (
              <div key={i} className="flex items-center justify-between py-1.5">
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>{item.product_nom}</p>
                <span className="text-xs font-bold" style={{ color: '#5DBCBF' }}>×{item.quantite || 1}</span>
              </div>
            ))}
          </div>
        )}

        {/* Infos */}
        <div className="rounded-2xl p-4 space-y-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-xs font-bold mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>VOTRE COMMANDE</p>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
            👤 {data.client_nom}
          </p>
          {data.date_evenement && (
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
              📅 {formatDateWithDay(data.date_evenement)}
            </p>
          )}
          {data.adresse && (
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
              📍 {data.adresse}
            </p>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
          Show Événement — 07 56 87 66 65 — Mis à jour automatiquement
        </p>
      </div>
    </div>
  );
}
